import { LogicalOperator } from 'estree';
import useGlobalFilterStore, { GlobalFilterSlice } from './globalFilterStore';
import { parseIfJSON } from 'utils/helpers';
import { filterOperators, getRootAndNestedFields } from 'utils/filterUtils';

export enum Operator {
  eq = 'eq',
  neq = 'neq',
  lt = 'lt',
  lte = 'lte',
  gt = 'gt',
  gte = 'gte',
  like = 'like',
  any = 'any',
  ilike = 'ilike',
  list = 'any',
}

export const TableFilterOperationLookup = {
  equals: Operator.eq,
  contains: Operator.ilike,
  arrIncludesSome: Operator.any,
  arrIncludes: Operator.any,
  betweenInclusive: [Operator.gte, Operator.lte],
} as const;

export interface Filter {
  value: string | number;
  operator: Operator;
}

export type FilterRepresentation = Record<string, Filter[]>;
export type JoinOperator = 'or' | 'and';

export interface EntityFilter {
  filter: FilterRepresentation;
  joinOperator: JoinOperator;
  setJoinOperator: (joinOperator: JoinOperator) => void;
  getQueryParamRepresentation: (respectGlobalFilters: boolean) => QueryParamRepresentation;
  getBuildDataFilterFunction: () => (d: any) => Boolean;
}

type QueryParamValue = `${Filter['operator']}:${Filter['value']}`;
type QueryParamRepresentation = Record<
  `filter[${string}]`,
  QueryParamValue | QueryParamValue[] | LogicalOperator
>;

export interface FilterStoreState {
  getQueryParamRepresentationByEntity: (
    entity: keyof this,
    respectedGlobalFilters: (keyof GlobalFilterSlice)[]
  ) => (respectGlobalFilters: boolean) => QueryParamRepresentation;
  getBuildDataFilterFunctionByEntity: (
    entity: keyof this,
    respectedGlobalFilters: (keyof GlobalFilterSlice)[]
  ) => (d: any) => Boolean;
}

const buildDataFilterFunction =
  (get) =>
  (
    entity: keyof FilterStoreState,
    respectedGlobalFilters: (keyof GlobalFilterSlice)[] = []
  ): ((d: any) => Boolean) => {
    const currentFilterState = get();
    const globalFilterState = useGlobalFilterStore.getState();

    if (!(entity in currentFilterState) && !(entity in globalFilterState)) return (d) => true;

    let entityFilter = {};
    if (currentFilterState[entity]) {
      entityFilter = {
        ...entityFilter,
        ...currentFilterState[entity].filter,
      };
    }

    respectedGlobalFilters.forEach((respectedFilter) => {
      if (respectedFilter in globalFilterState && globalFilterState[respectedFilter].filter) {
        const globalFilters = globalFilterState[respectedFilter].filter;
        Object.keys(globalFilters).forEach((key) => {
          entityFilter[key] = [...(entityFilter[key] || []), ...globalFilters[key]];
        });
      }
    });

    const useAndJoin = currentFilterState[entity]?.joinOperator === 'and';

    const filterFunc = (d) => {
      let include = useAndJoin ? true : false;
      const keys = Object.keys(entityFilter).filter((key) => entityFilter[key].length > 0);
      for (const field of keys) {
        const fieldFilter = entityFilter[field];
        const [rootField, nestedField] = getRootAndNestedFields(field);
        let rootValue = parseIfJSON(d.properties[rootField]) ?? d.properties[rootField];

        if (rootValue == null) {
          rootValue = {};
          if (!nestedField) {
            rootValue = null;
          }
        }
        let fieldMatches = false;
        const valuesToCheck = Array.isArray(rootValue) ? rootValue : [rootValue];
        fieldMatches = fieldFilter.some((filter) =>
          valuesToCheck.some((item) => {
            const valueToCompare = nestedField ? item[nestedField] ?? null : item;
            return filterOperators[filter.operator](valueToCompare, filter.value);
          })
        );

        if (useAndJoin) {
          include = include && fieldMatches;
          if (!include) break;
        } else {
          include = include || fieldMatches;
          if (include) break;
        }
      }
      return include;
    };
    return filterFunc;
  };

const getQueryParamRepresentationByEntityHelper =
  (get) =>
  (entity: keyof FilterStoreState, respectedGlobalFilters: string[] = []) =>
  (respectGlobalFilters = true): QueryParamRepresentation => {
    /*
    Preforms the transformation on filter, mapping arrays of type Filter to an
    object of the following format:

      {
        'filter[<fieldName>]' =  QueryParamValue[],
         ...
      }

    Example:
      {
        filter: [
          diameter: [
            {
              value: 1,
              operator: 'gte',
            },
            {
              value: 10,
              operator: 'lte',
            },
          ],
          address: [{
            value: '100 some street",
            operator: 'ilike',
          }]
        ]
      }

    Maps to:
      {
        'filter[diameter]' = ['gte:1', 'lte:10'],
        'filter[address]' = 'ilike: 100 some street',
      }
  */
    const queryParams: QueryParamRepresentation = {};
    const currentFilterState = get();

    if (!(entity in currentFilterState)) return queryParams;

    const entityFilter: EntityFilter = currentFilterState[entity].filter;
    const logicalOperator: LogicalOperator = currentFilterState[entity].joinOperator;

    const globalFilterState = useGlobalFilterStore.getState();

    Object.keys(entityFilter).forEach((field) => {
      const mapping = entityFilter[field]?.map(
        (filter: Filter) => `${filter.operator}:${filter.value}`
      );
      queryParams[`filter[${field}]`] = mapping?.length === 1 ? mapping[0] : mapping;
    });

    if (logicalOperator) {
      queryParams[`filter[joinBy]`] = logicalOperator;
    }

    respectGlobalFilters &&
      respectedGlobalFilters.forEach((globalFilterEntity) => {
        const globalEntityFilter: EntityFilter =
          globalFilterEntity in globalFilterState
            ? globalFilterState[globalFilterEntity]['filter']
            : [];
        Object.keys(globalEntityFilter).forEach((field) => {
          const fieldFilter = globalEntityFilter[field];
          if (fieldFilter.length === 0) return;

          const mapping = globalEntityFilter[field].map(
            (filter: Filter) => `${filter.operator}:${filter.value}`
          );
          queryParams[`filter[${field}]`] = mapping?.length === 1 ? mapping[0] : mapping;
        });
      });

    return queryParams;
  };

export const createFilterStore = <T extends FilterStoreState>(
  set: (setter: (prevState: T) => any) => void,
  get: () => T,
  filterStore: Omit<
    T,
    | 'getQueryParamRepresentationByEntity'
    | 'joinOperator'
    | 'setJoinOperator'
    | 'getBuildDataFilterFunctionByEntity'
  >
) => {
  return {
    ...filterStore,
    getQueryParamRepresentationByEntity: getQueryParamRepresentationByEntityHelper(get),
    getBuildDataFilterFunctionByEntity: buildDataFilterFunction(get),
  };
};
